import React, { useState, useEffect } from 'react';
import { Users, FileText, DollarSign, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import DashboardChart from '../components/DashboardChart';

const DashboardView = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStudents: 0,
    pendingApplications: 0,
    outstandingInvoices: 0,
    notifications: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data, error } = await supabase
          .from('students')
          .select('application_status');

        if (error) throw error;

        const stats = {
          totalStudents: data.length,
          pendingApplications: data.filter(s => s.application_status === 'pending').length,
          outstandingInvoices: data.filter(s => s.application_status === 'approved').length,
          notifications: data.filter(s => s.application_status === 'pending').length
        };

        setStats(stats);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (isLoading) return <LoadingSkeleton />;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
        <div className="text-sm text-gray-600">
          Logged in as: {user?.name} ({user?.role})
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { title: 'Total Students', value: stats.totalStudents, icon: Users, color: 'blue' },
          { title: 'Pending Applications', value: stats.pendingApplications, icon: FileText, color: 'yellow' },
          { title: 'Outstanding Invoices', value: stats.outstandingInvoices, icon: DollarSign, color: 'green' },
          { title: 'Notifications', value: stats.notifications, icon: Bell, color: 'purple' },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.title} className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-semibold mt-1">{stat.value}</p>
                </div>
                <Icon className={`h-8 w-8 text-${stat.color}-500`} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-6">Application Trends</h2>
        <DashboardChart
          totalStudents={stats.totalStudents}
          pendingLeases={stats.pendingApplications}
          outstandingInvoices={stats.outstandingInvoices}
        />
      </div>
    </div>
  );
};

export default DashboardView;